import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

interface BreadcrumbsProps {
  children: ReactNode;
  className?: string;
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ children, className = '' }) => {
  return (
    <nav aria-label="Breadcrumb" className={`breadcrumbs ${className}`}>
      <ol className="flex items-center gap-2 text-sm">
        {React.Children.map(children, (child, index) => {
          const isLast = index === React.Children.count(children) - 1;
          return (
            <React.Fragment key={index}>
              {child}
              {!isLast && (
                <li aria-hidden="true" className="text-slate-400 dark:text-slate-600">
                  <ChevronRight className="w-4 h-4" />
                </li>
              )}
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
};

interface BreadcrumbItemProps {
  href?: string;
  children: ReactNode;
  icon?: ReactNode;
  className?: string;
}

export const BreadcrumbItem: React.FC<BreadcrumbItemProps> = ({
  href,
  children,
  icon,
  className = '',
}) => {
  const isLink = !!href;

  const content = (
    <span className="flex items-center gap-1.5">
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span>{children}</span>
    </span>
  );

  if (isLink) {
    return (
      <li className={className}>
        <Link
          to={href}
          className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
        >
          {content}
        </Link>
      </li>
    );
  }

  return (
    <li className={`text-slate-900 dark:text-white font-semibold ${className}`} aria-current="page">
      {content}
    </li>
  );
};
